import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { namespaces, useSharedTranslation } from 'us-react-components/dist/hooks/useSharedTranslation';
import { axios, PayoutMethod, PayoutMethodService, PayoutMethodStatus } from 'us-web-services';

import { useDependencies } from '../../dependencies';
import { BranchActive } from './BranchActive';
import { BranchDeactivated } from './BranchDeactivated';
import { BranchInactive } from './BranchInactive';
import { PaymentDisabled } from './PaymentDisabled';

export const SitterPayout = observer((): JSX.Element => {
  document.title = 'UrbanSitter - Payout & Payment';
  const { t } = useSharedTranslation(namespaces.caregiver);
  const { stores } = useDependencies();
  const { sitter: { sitter }, user: { user } } = stores;

  const [loading, setLoading] = useState<boolean>(true);
  const [payoutMethod, setPayoutMethod] = useState<PayoutMethod>({} as PayoutMethod);
  const [jobId, setJobId] = useState<string>();

  const [searchParams] = useSearchParams();

  const getPayoutComponent = (): JSX.Element => {
    if (sitter?.paymentDisabled) {
      return <PaymentDisabled />;
    }

    if (payoutMethod?.status === PayoutMethodStatus.ACTIVE) {
      return <BranchActive payoutMethod={payoutMethod} />;
    }

    if (payoutMethod?.status === PayoutMethodStatus.CLOSED) {
      return <BranchDeactivated />;
    }

    return <BranchInactive jobId={jobId} />;
  };

  const getPayoutMethod = async (): Promise<void> => {
    const config = {
      params: {
        sitterId: user.id,
        userId: user.id,
      }
    };

    try {
      const response = (await PayoutMethodService.findByFilter(config)).data;
      setPayoutMethod(response.data[0]);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data.errors) {
        toast.error(e.response.data.errors[0].message);
      } else {
        console.warn(e);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fromJobId = searchParams.get('jobId');

    if (fromJobId) {
      setJobId(fromJobId);
    }

    getPayoutMethod();
  }, [searchParams]);

  return (
    <article className='container py-3'>
      {loading && <h1 className='text-2xl font-semibold py-5'>{t('common.loading')}</h1>}
      {!loading && <div>{getPayoutComponent()}</div>}
    </article>
  );
});
